import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCalendarCheck,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import Carousel from "./components/Carousel";
import Loading from "../../Loading";
import { FiCalendar, FiInfo, FiCheckCircle } from "react-icons/fi";
import {
  Calendar,
  Clock,
  User as UserIcon,
  CreditCard,
  Edit,
  Activity,
  FileText,
} from "lucide-react";
import user from "../../../assets/image/user.jpg";
import logo from "../../../assets/image/kkgt-header-image.jpg";

// Simplified animations without external dependencies
const AnimatedContainer = ({ children, className }) => (
  <div className={`transition-all duration-300 ease-in-out ${className}`}>
    {children}
  </div>
);

const PatientDashboard = () => {
  const {
    loginUser,
    getUserDetails,
    userDetails,
    userDetailsLoading,
    userDetailsError,
  } = useContext(AuthContext);

  // Fetch existing medical details on component mount
  useEffect(() => {
    if (userDetails?.length === 0) {
      getUserDetails(loginUser.id);
    }
  }, [userDetails, getUserDetails, loginUser.id]);

  // Extract data from userDetails
  const appointmentsData = userDetails?.appointmentDetails || [];
  const opdFormsData = userDetails?.opdForms || [];
  const profileData = userDetails?.userDetails || {};

  const [dataRefreshTime, setDataRefreshTime] = useState(new Date());

  // Build billing data with proper pending calculation based on actual data structure
  const billingData = useMemo(() => {
    const billing = [];
    
    opdFormsData.forEach((opdForm) => {
      const patientReport = opdForm.patientReport;
      const visits = patientReport?.financials?.visits || [];
      const billingItems = patientReport?.financials?.billingItems || [];
      const opdNumber = patientReport?.opdNumber || opdForm.opdNumber;
      
      // Calculate total cost for this OPD from billing items
      const totalCost = billingItems.reduce((sum, item) => {
        // Handle different possible structures for totalCost
        const itemCost = item.totalCost || item.totalCostPerItem || item.amount || 0;
        return sum + itemCost;
      }, 0);
      
      // Calculate total paid amount for this OPD (only completed treatments)
      const totalPaid = visits
        .filter(visit => visit.treatmentDone === true)
        .reduce((sum, visit) => sum + (visit.amount || 0), 0);
      
      // Calculate pending amount for this OPD
      const pendingAmount = Math.max(0, totalCost - totalPaid);
      
      visits.forEach((visit) => {
        billing.push({
          id: visit._id,
          opd: opdNumber,
          date: visit.date,
          amount: visit.amount || 0,
          totalCost: totalCost,
          totalPaid: totalPaid,
          pendingAmount: pendingAmount,
          status: visit.treatmentDone ? "Paid" : "Pending",
          treatmentDone: visit.treatmentDone,
          reason: visit.reason,
          notes: visit.notes
        });
      });

      // If no visits but there are billing items, create entry for pending amount
      if (visits.length === 0 && billingItems.length > 0) {
        billing.push({
          id: `${opdNumber}-pending`,
          opd: opdNumber,
          date: opdForm.createdAt || new Date(),
          amount: 0,
          totalCost: totalCost,
          totalPaid: 0,
          pendingAmount: totalCost,
          status: "Pending",
          treatmentDone: false,
          reason: "Initial Treatment",
          notes: ""
        });
      }
    });
    
    return billing;
  }, [opdFormsData]);

  // Calculate total pending payments across all OPDs
  const totalPendingPayments = useMemo(() => {
    const opdPendingMap = {};
    
    // Group by OPD and calculate pending for each
    billingData.forEach(item => {
      if (!opdPendingMap[item.opd]) {
        opdPendingMap[item.opd] = item.pendingAmount;
      }
    });
    
    // Sum all pending amounts
    return Object.values(opdPendingMap).reduce((total, pending) => total + pending, 0);
  }, [billingData]);

  const today = new Date();
  const todayMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const upcomingAppointments = useMemo(() => {
    return appointmentsData.filter(
      (appointment) => new Date(appointment.date) >= today
    );
  }, [appointmentsData, today]);

  // Filter for today's appointments
  const todayAppointments = useMemo(() => {
    return appointmentsData.filter((appointment) => {
      const appointmentDate = new Date(appointment.date);
      return (
        appointmentDate.getFullYear() === todayMidnight.getFullYear() &&
        appointmentDate.getMonth() === todayMidnight.getMonth() &&
        appointmentDate.getDate() === todayMidnight.getDate()
      );
    });
  }, [appointmentsData, todayMidnight]);

  // Extract visits data with memoization
  const visitsData = useMemo(() => {
    const visits = [];
    opdFormsData.forEach((opdForm) => {
      const patientReport = opdForm.patientReport;
      const visitList = patientReport?.financials?.visits || [];
      visitList.forEach((visit) => {
        visits.push({
          id: visit._id,
          checkupFor: opdForm.checkupInfo,
          opdNumber: opdForm.opdNumber,
          date: visit.date,
          reason: visit.reason,
          treatmentDone: visit.treatmentDone,
          notes: visit.notes,
        });
      });
    });
    return visits;
  }, [opdFormsData]);

  const upcomingVisits = useMemo(() => {
    return visitsData.filter((visit) => {
      const visitDate = new Date(visit.date);
      return visitDate > today;
    });
  }, [visitsData, today]);

  // Filter for today's visits
  const todayVisits = useMemo(() => {
    return visitsData.filter((visit) => {
      const visitDate = new Date(visit.date);
      return (
        visitDate.getFullYear() === todayMidnight.getFullYear() &&
        visitDate.getMonth() === todayMidnight.getMonth() &&
        visitDate.getDate() === todayMidnight.getDate()
      );
    });
  }, [visitsData, todayMidnight]);

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }, []);

  // Memoized Stats Cards Section
  const StatsCardsSection = useMemo(
    () => (
      <div className="shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <Activity size={24} className="text-blue-600" />
          Dashboard Overview
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Upcoming Visits */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 shadow-lg rounded-lg p-6 flex items-center hover:shadow-xl transition-all transform hover:scale-105">
            <div className="bg-green-500 p-3 rounded-full">
              <FaCalendarAlt className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-green-600">
                {upcomingVisits.length}
              </p>
              <p className="text-gray-700 font-medium">Upcoming Visits</p>
              <p className="text-xs text-gray-500">
                Total: {visitsData.length}
              </p>
            </div>
          </div>

          {/* Total Appointments */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 shadow-lg rounded-lg p-6 flex items-center hover:shadow-xl transition-all transform hover:scale-105">
            <div className="bg-blue-500 p-3 rounded-full">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-blue-600">
                {upcomingAppointments.length}
              </p>
              <p className="text-gray-700 font-medium">Upcoming Appointments</p>
              <p className="text-xs text-gray-500">
                Total: {appointmentsData.length}
              </p>
            </div>
          </div>

          {/* Pending Payments */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 shadow-lg rounded-lg p-6 flex items-center hover:shadow-xl transition-all transform hover:scale-105">
            <div className="bg-yellow-500 p-3 rounded-full">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-yellow-600">
                ₹{totalPendingPayments.toLocaleString()}
              </p>
              <p className="text-gray-700 font-medium">Pending</p>
              <p className="text-xs text-gray-500">Payments</p>
            </div>
          </div>

          {/* Edit Profile */}
          <Link to="/dashboard/profile">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 shadow-lg rounded-lg p-6 flex items-center hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer">
              <div className="bg-purple-500 p-3 rounded-full">
                <Edit className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-lg font-bold text-purple-600">
                  Edit Profile
                </p>
                <p className="text-gray-700 font-medium">Update Info</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    ),
    [
      upcomingVisits.length,
      visitsData.length,
      upcomingAppointments.length,
      appointmentsData.length,
      totalPendingPayments,
    ]
  );

  if (userDetailsLoading) {
    return <Loading />;
  }

  if (userDetailsError) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-red-500 text-xl">{userDetailsError}</div>
      </div>
    );
  }

  return (
    <AnimatedContainer className="mx-auto min-h-screen">
      {/* Carousel Section */}
      <div className="mb-6">
        <Carousel />
      </div>

      {/* Stats Cards */}
      {StatsCardsSection}

      {/* Today's Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Today's Visits */}
        <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <FaCalendarCheck size={24} className="text-green-600" />
            Today's Visits
          </h2>

          <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto">
            {todayVisits.length > 0 ? (
              <div className="space-y-4">
                {todayVisits.map((visit) => (
                  <div
                    key={visit.id}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-full">
                        <FaCalendarCheck className="text-green-600" size={16} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Visit scheduled for{" "}
                          <span className="font-semibold text-green-700">
                            {visit.reason || "treatment"}
                          </span>{" "}
                          related to{" "}
                          <span className="font-semibold text-gray-800">
                            {visit.checkupFor}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          OPD:{" "}
                          <span className="font-medium">{visit.opdNumber}</span>
                        </p>
                        <div className="mt-2">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              visit.treatmentDone
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {visit.treatmentDone ? "Completed" : "Pending"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <FaCalendarCheck
                    size={48}
                    className="mx-auto mb-4 text-gray-300"
                  />
                  <p>No visits scheduled for today</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Today's Appointments */}
        <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <Calendar size={24} className="text-blue-600" />
            Today's Appointments
          </h2>

          <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto">
            {todayAppointments.length > 0 ? (
              <div className="space-y-4">
                {todayAppointments.map((appointment) => (
                  <div
                    key={appointment._id}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <FiCalendar className="text-blue-500 text-lg" />
                        <h4 className="text-gray-800 font-semibold">
                          Appointment Details
                        </h4>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-blue-500" />
                        <span className="text-sm font-semibold text-gray-800">
                          {appointment.timeBlock}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <FiInfo size={16} className="text-gray-500" />
                        <span className="text-sm text-gray-700">
                          {appointment.reason}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <FiCheckCircle size={16} className="text-gray-500" />
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            appointment.status === "Completed"
                              ? "bg-green-100 text-green-800"
                              : appointment.status === "Pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {appointment.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No appointments for today</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Upcoming Visits */}
        <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <FaCalendarAlt size={24} className="text-green-600" />
            Upcoming Visits
          </h2>

          <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto">
            {upcomingVisits.length > 0 ? (
              <div className="space-y-4">
                {upcomingVisits.map((visit) => (
                  <div
                    key={visit.id}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-full">
                        <FaCalendarAlt className="text-green-600" size={16} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock size={14} className="text-gray-500" />
                          <span className="text-sm font-semibold text-gray-800">
                            {formatDate(visit.date)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-green-700">
                            {visit.reason || "Treatment"}
                          </span>{" "}
                          for{" "}
                          <span className="font-semibold text-gray-800">
                            {visit.checkupFor}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          OPD:{" "}
                          <span className="font-medium">{visit.opdNumber}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <FaCalendarAlt
                    size={48}
                    className="mx-auto mb-4 text-gray-300"
                  />
                  <p>No upcoming visits</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <Calendar size={24} className="text-blue-600" />
            Upcoming Appointments
          </h2>

          <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto">
            {upcomingAppointments?.length > 0 ? (
              <div className="space-y-4">
                {upcomingAppointments?.map((appointment) => (
                  <div
                    key={appointment._id}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <FiCalendar className="text-blue-500 text-lg" />
                        <h4 className="text-gray-800 font-semibold">
                          Appointment Details
                        </h4>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-blue-500" />
                        <span className="text-sm font-semibold text-gray-800">
                          {formatDate(appointment.date)} -{" "}
                          {appointment.timeBlock}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <FiInfo size={16} className="text-gray-500" />
                        <span className="text-sm text-gray-700">
                          {appointment.reason}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <FiCheckCircle size={16} className="text-gray-500" />
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            appointment.status === "Completed"
                              ? "bg-green-100 text-green-800"
                              : appointment.status === "Pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {appointment.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No upcoming appointments</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Billing Information */}
      <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <FaMoneyBillWave size={24} className="text-yellow-600" />
          Billing Information
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 rounded-lg shadow-md">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="py-3 px-6 text-left font-semibold border border-gray-300">
                  OPD No.
                </th>
                <th className="py-3 px-6 text-left font-semibold border border-gray-300">
                  Date
                </th>
                <th className="py-3 px-6 text-left font-semibold border border-gray-300">
                  Visit Amount
                </th>
                <th className="py-3 px-6 text-left font-semibold border border-gray-300">
                  Total Cost
                </th>
                <th className="py-3 px-6 text-left font-semibold border border-gray-300">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {billingData.length > 0 ? (
                billingData
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="bg-white border-b hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 font-medium text-blue-600 border border-gray-300">
                        {invoice.opd}
                      </td>
                      <td className="py-4 px-6 text-gray-700 border border-gray-300">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="py-4 px-6 font-semibold text-gray-800 border border-gray-300">
                        ₹{invoice.amount}
                      </td>
                      <td className="py-4 px-6 font-semibold text-gray-800 border border-gray-300">
                        ₹{invoice.totalCost}
                      </td>
                      <td className="py-4 px-6 border border-gray-300">
                        <div className="space-y-1">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              invoice.status === "Paid"
                                ? "bg-green-100 text-green-800"
                                : invoice.status === "Pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {invoice.status}
                          </span>
                          {invoice.pendingAmount > 0 && (
                            <div className="text-xs text-red-600 font-medium">
                              Pending: ₹{invoice.pendingAmount}
                            </div>
                          )}
                          {invoice.totalPaid > 0 && (
                            <div className="text-xs text-green-600 font-medium">
                              Paid: ₹{invoice.totalPaid}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td
                    className="py-8 px-6 text-center text-gray-500"
                    colSpan="5"
                  >
                    <div className="flex flex-col items-center">
                      <FaMoneyBillWave
                        size={48}
                        className="mb-4 text-gray-300"
                      />
                      <p>No billing information available</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Section */}
        {billingData.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Total Cost</h3>
              <p className="text-2xl font-bold text-blue-600">
                ₹{billingData.reduce((acc, item) => acc + item.totalCost, 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h3 className="text-lg font-semibold text-green-800 mb-2">Total Paid</h3>
              <p className="text-2xl font-bold text-green-600">
                ₹{billingData.reduce((acc, item) => acc + item.totalPaid, 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Total Pending</h3>
              <p className="text-2xl font-bold text-yellow-600">
                ₹{totalPendingPayments.toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </div>
    </AnimatedContainer>
  );
};

export default PatientDashboard;